
import React, { useState, useEffect } from 'react';
import { Professional, ListingTier } from '../types';
import { listingService } from '../services/listingService';
import { authService } from '../services/authService';
import { useNavigate } from 'react-router-dom';
import { 
  Table as TableIcon, 
  Search, 
  Trash2, 
  RefreshCw,
  Edit2,
  Database,
  Wifi,
  FileSpreadsheet,
  Info,
  ShieldCheck,
  ShieldAlert,
  ChevronRight,
  HardDrive,
  Check,
  Lock,
  Loader2,
  Users,
  Target
} from 'lucide-react';
import { Link } from 'react-router-dom';

const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const [listings, setListings] = useState<Professional[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [storageMode, setStorageMode] = useState<'cloud' | 'local'>('local');
  const [showExportToast, setShowExportToast] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [verifyingId, setVerifyingId] = useState<string | null>(null);

  const ADMIN_EMAIL = 'planspace@outlook.com';

  useEffect(() => {
    const user = authService.getUser();
    if (!user || user.email !== ADMIN_EMAIL) {
      navigate('/');
      return;
    }
    setIsAuthorized(true);
    loadData();
  }, [navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await listingService.getAllListings();
      setListings(data);
      
      const response = await fetch('/api/listings?test=true').catch(() => null);
      setStorageMode(response?.ok ? 'cloud' : 'local');
    } catch (err) {
      setStorageMode('local');
    } finally {
      setLoading(false);
    }
  };

  const handleManualVerify = async (id: string) => {
    setVerifyingId(id);
    const success = await listingService.verifyListing(id);
    if (success) {
      await loadData();
    }
    setVerifyingId(null);
  };

  const downloadCSV = () => {
    if (listings.length === 0) {
      alert("Spreadsheet is empty.");
      return;
    }

    const headers = [
      'ID', 'Business Name', 'Category', 'Tier', 'Rating', 'Reviews', 
      'ABN', 'Suburb', 'State', 'Email', 'Phone', 'Verified', 'Created At'
    ];

    const rows = listings.map(p => [
      p.id,
      `"${p.businessName.replace(/"/g, '""')}"`,
      `"${p.category}"`,
      p.tier,
      p.rating,
      p.reviewCount,
      `"${p.abn || ''}"`,
      `"${p.location.suburb}"`,
      p.location.state,
      `"${p.contact.email}"`,
      `"${p.contact.phone}"`,
      p.isVerified ? 'YES' : 'NO',
      p.createdAt
    ]);

    const csvContent = "\uFEFF" + [
      headers.join(','),
      ...rows.map(r => r.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const timestamp = new Date().toISOString().split('T')[0];
    
    link.setAttribute('href', url);
    link.setAttribute('download', `planstack_directory_${timestamp}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setShowExportToast(true);
    setTimeout(() => setShowExportToast(false), 4000);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Delete this listing?')) {
      await listingService.deleteListing(id);
      loadData();
    }
  };

  if (!isAuthorized) return null;

  const filtered = listings.filter(l => 
    l.businessName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    l.location.suburb.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const verifiedCount = listings.filter(l => l.isVerified).length;
  const verificationTarget = 100;
  const progress = Math.min((verifiedCount / verificationTarget) * 100, 100);

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-[1600px] mx-auto">
        
        {showExportToast && (
          <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[100] animate-fade-in">
             <div className="bg-brand-900 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-4 border border-brand-700">
                <div className="bg-green-500 p-1.5 rounded-full"><Check size={16} /></div>
                <div>
                   <p className="text-sm font-black uppercase tracking-tight">Export Successful</p>
                   <p className="text-[10px] text-brand-300 font-bold uppercase tracking-widest">Check Downloads folder</p>
                </div>
             </div>
          </div>
        )}

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
          <div>
            <h1 className="text-2xl font-black text-gray-900 uppercase tracking-tight flex items-center gap-3">
              <Lock className="text-red-600" size={24} /> Master Directory Admin
            </h1>
            <p className="text-gray-500 font-medium italic">Exclusive administrative access for {ADMIN_EMAIL}</p>
          </div>
          
          <div className="flex flex-wrap gap-3">
             <div className={`flex items-center gap-2 px-4 py-3 rounded-xl border text-[10px] font-black uppercase tracking-widest transition-all ${
               storageMode === 'cloud' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-amber-50 border-amber-200 text-amber-700'
             }`}>
               {storageMode === 'cloud' ? <Wifi size={14} /> : <HardDrive size={14} />}
               {storageMode === 'cloud' ? 'Cloud Sync' : 'Local Only'}
             </div>
             
             <button onClick={downloadCSV} className="bg-brand-900 text-white px-6 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest flex items-center gap-2 hover:bg-black transition shadow-lg active:scale-95">
                <FileSpreadsheet size={16} /> Export CSV
             </button>

             <button onClick={loadData} className="p-3 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition">
                <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
             </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
           <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <div className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-1">Total Listings</div>
              <div className="text-3xl font-black text-gray-900">{listings.length}</div>
           </div>
           <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <div className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-1">Premium Accounts</div>
              <div className="text-3xl font-black text-brand-600">
                {listings.filter(p => p.tier === 'EXCLUSIVE' || p.tier === 'PREMIUM').length}
              </div>
           </div>
           
           {/* E-E-A-T Goal Tracker */}
           <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 lg:col-span-2 relative overflow-hidden">
              <div className="flex justify-between items-start relative z-10">
                 <div>
                    <div className="text-[10px] font-black text-blue-500 uppercase tracking-[0.2em] mb-1">Human Vetting Goal (E-E-A-T)</div>
                    <div className="text-3xl font-black text-gray-900">{verifiedCount} / {verificationTarget} <span className="text-xs font-bold text-gray-400">Professionals</span></div>
                 </div>
                 <div className="bg-blue-50 text-blue-600 p-2 rounded-lg">
                    <Target size={24} />
                 </div>
              </div>
              <div className="mt-4 w-full bg-gray-100 h-2 rounded-full overflow-hidden relative z-10">
                 <div className="h-full bg-blue-600 transition-all duration-1000" style={{ width: `${progress}%` }}></div>
              </div>
              <p className="mt-2 text-[9px] font-bold text-gray-400 uppercase tracking-widest relative z-10">
                 Vetting the first {verificationTarget} firms manually establishes critical domain authority for Google search.
              </p>
           </div>
        </div>

        <div className="bg-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-100 bg-white sticky top-0 z-10 flex gap-4">
             <div className="relative flex-grow">
               <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
               <input 
                 type="text" 
                 placeholder="Filter directory records by business name or suburb..." 
                 className="w-full pl-12 pr-4 py-3 bg-gray-50 border-none rounded-xl outline-none focus:ring-2 focus:ring-brand-500 transition shadow-inner font-medium"
                 value={searchTerm}
                 onChange={(e) => setSearchTerm(e.target.value)}
               />
             </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="p-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Status</th>
                  <th className="p-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Practice</th>
                  <th className="p-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Tier</th>
                  <th className="p-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Location</th>
                  <th className="p-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Human Vetting</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((pro) => (
                  <tr key={pro.id} className="hover:bg-brand-50/20 transition group">
                    <td className="p-4">
                      <div className={`flex items-center gap-2 font-bold text-[10px] uppercase ${pro.isVerified ? 'text-green-600' : 'text-amber-500'}`}>
                         {pro.isVerified ? <ShieldCheck size={16} /> : <ShieldAlert size={16} />} 
                         {pro.isVerified ? 'Verified' : 'Pending'}
                      </div>
                    </td>
                    <td className="p-4">
                       <div className="font-bold text-gray-900 text-sm">{pro.businessName}</div>
                       <div className="text-[9px] text-gray-400 font-mono mt-0.5">{pro.id}</div>
                    </td>
                    <td className="p-4">
                      <span className={`text-[9px] font-black uppercase px-2 py-1 rounded-md ${
                        pro.tier === ListingTier.EXCLUSIVE ? 'bg-amber-100 text-amber-800' :
                        pro.tier === ListingTier.PREMIUM ? 'bg-brand-100 text-brand-800' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {pro.tier}
                      </span>
                    </td>
                    <td className="p-4 text-xs font-medium text-gray-500">{pro.location.suburb}, {pro.location.state}</td>
                    <td className="p-4 text-right space-x-1">
                      {!pro.isVerified ? (
                        <button 
                          onClick={() => handleManualVerify(pro.id)}
                          disabled={verifyingId === pro.id}
                          className="bg-green-600 text-white px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 hover:bg-green-700 transition shadow-sm"
                          title="Manual Vetting Complete"
                        >
                          {verifyingId === pro.id ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                          Verify Listing
                        </button>
                      ) : (
                        <button 
                          disabled
                          className="bg-gray-100 text-gray-400 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 cursor-default"
                        >
                          <ShieldCheck size={12} />
                          Human Vetted
                        </button>
                      )}
                      <Link to={`/listing-setup?edit=${pro.id}`} className="p-2.5 text-gray-300 hover:text-brand-600 inline-block transition hover:bg-white rounded-xl shadow-sm border border-transparent hover:border-brand-100" title="Edit Listing">
                        <Edit2 size={16} />
                      </Link>
                      <button onClick={() => handleDelete(pro.id)} className="p-2.5 text-gray-300 hover:text-red-600 transition hover:bg-white rounded-xl shadow-sm border border-transparent hover:border-red-100" title="Delete Listing">
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
